// ── Is the system being updated, and is its API answering? (Bugify deploy-now, owner 2026-11-03) ───────────────────
// "注意部署时，增加等待进度条，确保客户看起来不是系统死机" — while a deploy restarts an installation, the staff and the
// clients on its portal must see that it is being updated, not that it died. Two signals, both cheap:
//   • build/deploy-status.json, written by deploy.sh on the server it deploys (utils/deployStatus.ts says how it is
//     read): every 15 s while the tab is in front (STATUS_IDLE_MS; none while it is hidden), every 5 s while a deploy is
//     on, at once when the tab comes back to the front, at once on any API trouble, and again with each failed probe.
//     It is a static file nginx serves — none of these reads reaches the API.
//   • GET /api/health, probed at once when a request fails (utils/api.ts reportApiTrouble) and then every 3 s — or,
//     while the file says the server is restarting, before anything has failed — until it answers again. Only while the
//     tab is in front: a hidden tab sends nothing.
// components/DeployNotice.tsx draws what this holds. Module-level singleton with subscribe(), the same pattern as
// sandboxMode.ts: it outlives route changes and ends with the tab.
//
// Live check 2026-11-04 (a backend-only redeploy of test: "deploying" 5 s, then "restarting" and 19 s without an API):
// an idle Bugify page showed nothing for 16 s, then the pill with the wrong sentence, never the card. What changed:
//   • the idle read every 15 s instead of 30 s, so a "restarting" that lasts ~20 s is always seen during it;
//   • a failed request probes /api/health at once (was 1 s later) and reads the file at once (it was skipped when a read
//     had started within 3 s); a failed probe reads the file again, so a restart announced since the last read is seen;
//   • the card needs ONE failed probe when the file says a deploy is on (deployStatus.ts overlayWanted), two otherwise;
//   • once up, the card stays until the API answers, then says "Back online" — at least 2.5 s in all (backHoldMs);
//   • restartBack (the pill's "finishing") needs only that the API failed once during the restart, or answered well
//     after the restart was written (restartLooksDone) — no longer two failed probes.
// Review round 1 of that change:
//   • the one-probe card is for "restarting" only; "deploying" (minutes of building, the API still up) keeps the
//     two-probe rule, so a network blip in those minutes does not block the page;
//   • a busy status is trusted only while a recent read confirms it (deployStatus.ts freshState): a "restarting"
//     remembered from before the laptop slept is not taken for a deploy when the network comes back slowly;
//   • an answer counts as "back" only when it was asked for after the trouble began: the old process finishes its
//     in-flight requests while the new one is not up yet, and that is not "the update has finished";
//   • a hidden tab sends nothing at all — no status read, no probe; it looks again when it comes back to the front;
//   • the card's "Back online" says "the update has finished" only once the file is idle and the outage was a
//     restart's; after it, the pill does not add a spinning "finishing" (backSaid);
//   • the card steps down to the banner once no deploy explains the outage any more (deployExplains), as before.
// Review round 2:
//   • "back" during a restart is proven by the process, not the clock: /api/health says when its process started
//     (`boot`), and a 200 from a process older than the "restarting" write is the one being replaced — still answering
//     because its event loop was blocked when pm2 signalled it — so it neither ends the outage nor counts as the restart
//     being behind us (deployStatus.ts bootPredatesRestart). Real requests' answers no longer end an outage on this
//     installation while it is restarting: the probes decide;
//   • a portal pinned to ANOTHER installation is not blocked by this one's restart: no restart watch, no card
//     (startApiHealth's `pinnedElsewhere`, from DeployNotice) — the pill only, and the banner if its own host fails;
//   • a "restarting" that this tab has seen through and that is 90 s old counts as idle (deployStatus.ts
//     restartSettled): a deploy that died before writing idle no longer keeps every tab probing for 15 minutes;
//   • a failed request always brings a status read sent AFTER the trouble began: a read already on the wire is followed
//     by another as soon as it lands (statusOwed) — its answer may predate the restart;
//   • a deploy seen earlier explains only an outage that began while it was on (deployStatus.ts deployTailExplains),
//     not any outage within 15 minutes of it: a client's Wi-Fi dropping after a release gets the banner.
// Nothing here retries a failed request; the screens that care listen for slf:api-back.
//
// ⛔ This file must not import utils/api.ts: api.ts imports it (reportApiTrouble), and a cycle between the two would
// hand one of them a half-built module at start-up. Hence ownApiBase() below, and the loaded build passed in by
// startApiHealth() rather than read from utils/appUpdate.tsx (which reaches the store, which reaches api.ts).
import {
  DeployStatus, ProbeState, PROBE_IDLE, parseDeployStatus, isNewBuild, probeTrouble, probeStep, DEPLOY_STATUS_MAX_AGE_MS,
  STATUS_IDLE_MS, STATUS_BUSY_MS, BACK_SHOW_MS, overlayWanted, backHoldMs, restartLooksDone, freshState, DeployState,
  bootPredatesRestart, restartSettled, deployTailExplains,
} from '../utils/deployStatus';

export interface ApiHealth {
  probe: ProbeState;
  probeBase: string;             // the API host the last trouble was on
  status: DeployStatus;
  statusSeenAt: number;          // client ms of the last status file read (0 = none yet)
  busySeenAt: number;            // client ms at which a fresh deploying/restarting status was last seen (0 = never)
  idleSeenAt: number;            // client ms of the FIRST read that found the file idle after the last busy one (0 = never):
                                 // did the deploy end before an outage began? (deployExplains)
  outageAt: number;              // client ms at which the current (or last) outage began (0 = none yet)
  busySince: number;             // server ms: when THIS deploy began (its first non-idle startedAt; 0 = idle). deploy.sh
                                 // stamps every write afresh, so "restarting" alone would restart the counter at 0:00
  restartBack: boolean;          // the file says "restarting" and the restart is behind us: a request or probe failed
                                 // meanwhile and the API answers again, or it answered well after the write
                                 // (deployStatus.ts restartLooksDone)
  serverOffsetMs: number;        // server clock − client clock, from the status response's Date header
  newBuild: boolean;
  restartOutage: boolean;        // the outage now on (or whose "Back online" is showing) overlapped a "restarting":
                                 // only then does the card's "Back online" speak of the update
  backSaid: boolean;             // the card has said "Back online" for this restart: the pill adds no "finishing"
  back: '' | 'overlay' | 'banner';   // "Back online" is showing, in the place the outage was shown
  dismissedOverlay: boolean;     // "Continue anyway" — the outage shows as a banner instead, until the API is back
  overlay: boolean;              // derived: the blocking "Updating — reconnecting" card is up (only for a deploy's outage)
}

const PROBE_EVERY_MS = 3000, PROBE_TIMEOUT_MS = 5000;
// A failed probe re-reads the status file unless a read was sent since the outage began and within STATUS_AFTER_PROBE_MS
// — so an outage costs at most one (static) status read per probe.
const STATUS_AFTER_PROBE_MS = 2500;

/** This build's own API host. The same expression as DEFAULT_BASE in utils/api.ts, which this file must not import
 *  (see the top). Only this host can raise the blocking card: a portal pinned to another installation cannot reach
 *  that one's /api/health from here when its CORS predates this feature, and must never be blocked by it. */
export const ownApiBase = (): string => process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

let state: ApiHealth = {
  probe: { ...PROBE_IDLE }, probeBase: '', status: { state: 'idle', build: '', startedAt: 0, stale: false },
  statusSeenAt: 0, busySeenAt: 0, idleSeenAt: 0, outageAt: 0, busySince: 0, restartBack: false, serverOffsetMs: 0, newBuild: false,
  restartOutage: false, backSaid: false, back: '', dismissedOverlay: false, overlay: false,
};
const subs = new Set<(s: ApiHealth) => void>();
let started = false;
let loadedBuild: () => string = () => '';
let chunkFailed = false;
let probeTimer: number | undefined;
let probing = false;             // a probe chain is scheduled or in flight
let probeGen = 0;                // bumped by every (re)schedule and stop: an answer from an older probe is dropped
let probesInFlight = 0;          // a probe on the wire is never rescheduled to "now" — it is about to answer
let statusTimer: number | undefined;
let statusBusy = false;
let statusTriedAt = 0;           // client ms of the last status read started — failures come in bursts, reads need not
let statusUrl = '';              // the candidate that answered with a real v1 file, once one has
let backTimer: number | undefined;
let overlayShownAt = 0;          // client ms at which the card last went up
let outageMark = 0;              // bumped each time an outage begins (probe.probing turns on): see reportApiOk
let statusOwed = false;          // a read was asked for while one sent earlier was on the wire: read again when it lands
// Does this page work against ANOTHER installation's API (a client portal pinned there: utils/api.ts PORTAL_SYSTEMS)?
// Then this installation's restart is not theirs: no restart watch, no blocking card (review round 2). Passed in by
// DeployNotice (startApiHealth), since this file must not import utils/api.ts.
let pinnedElsewhere: () => boolean = () => false;
const ownWorks = (): boolean => { try { return !pinnedElsewhere(); } catch { return true; } };

/** What the status file says now, as far as a RECENT read of it shows (deployStatus.ts freshState). Everything that acts
 *  on the file — the card, the pill, the probe watch — goes by this, never by the last value remembered. A restart this
 *  tab has seen through, written 90 s ago or more, is over whatever the file still says (restartSettled, review round 2:
 *  a deploy that died before it could write idle). */
export const statusNow = (s: ApiHealth, nowMs = Date.now()): DeployState => {
  const st = freshState(s.status, s.statusSeenAt, nowMs);
  return st === 'restarting' && restartSettled(s.status, s.restartBack, nowMs + (s.serverOffsetMs || 0)) ? 'idle' : st;
};
/** Is this outage a deploy's? A recent read of the server's status file says one is on, or the outage began while one
 *  was (deployStatus.ts deployTailExplains: within a minute of a busy read, no idle read in between, and that busy read
 *  less than 15 min ago — review round 2: it used to be any outage within 15 min of a deploy, a client's own Wi-Fi drop
 *  included). Times are this tab's clock, so a tab woken hours later does not count an old read. */
export const deployExplains = (s: ApiHealth, nowMs = Date.now()): boolean =>
  statusNow(s, nowMs) !== 'idle' || deployTailExplains(s.busySeenAt, s.idleSeenAt || 0, s.probe.since || s.outageAt || 0, nowMs);
/** The blocking card: this build's own API does not answer AND a deploy explains it (review round 2) — one failed probe
 *  while the file says the server is restarting, two otherwise (deployStatus.ts overlayWanted, live check 2026-11-04 and
 *  its review round 1). A plain lost connection — a phone on a bad network, a client's Wi-Fi dropping — only gets the
 *  non-blocking "Reconnecting…" banner: making the chat, a half-written email or the portal unreachable on every network
 *  blip is not what the owner asked for. */
export const isOverlay = (s: ApiHealth, nowMs = Date.now()): boolean =>
  s.probeBase === ownApiBase() && ownWorks() && !s.dismissedOverlay && overlayWanted(s.probe, statusNow(s, nowMs) === 'restarting', deployExplains(s, nowMs));

/** Is the card up after this change? Once up, it stays until the API answers (and then says so) or the person continues
 *  anyway: a status file that turns idle while this tab still cannot reach its API must not take it away without a word.
 *  It steps down to the banner once no deploy explains the outage any more (deployExplains: 15 min after the last busy
 *  read at most), as it did before the card became sticky (review round 1). */
export const overlayAfter = (prevOverlay: boolean, next: ApiHealth, nowMs = Date.now()): boolean =>
  isOverlay(next, nowMs) || next.back === 'overlay'
  || (prevOverlay && next.probe.probing && next.probeBase === ownApiBase() && ownWorks() && !next.dismissedOverlay && deployExplains(next, nowMs));

/** Which outage this tab is in (or was last in). utils/api.ts reads it as a request goes out and hands it back with the
 *  answer (reportApiOk): an answer to a request sent before the current outage began is not news about it. A counter,
 *  not a clock, so a wall clock set back cannot make every answer look old. */
export const apiOutageMark = (): number => outageMark;

function set(patch: Partial<ApiHealth>): void {
  const prev = state;
  const next = { ...state, ...patch };
  if (next.probe.probing && !prev.probe.probing) outageMark++;
  // When this outage began — kept after it, while its "Back online" shows (deployExplains asks).
  if (next.probe.probing) next.outageAt = next.probe.since || next.outageAt || Date.now();
  next.overlay = overlayAfter(prev.overlay, next);
  if (next.overlay && !prev.overlay) overlayShownAt = Date.now();
  // Was this outage a restart's? Decided while it lasts, kept while its "Back online" shows, forgotten after.
  next.restartOutage = next.probe.probing ? (prev.restartOutage || statusNow(next) === 'restarting')
    : next.back ? prev.restartOutage : false;
  state = next;
  subs.forEach(f => { try { f(state); } catch { /* a bad subscriber must not stop the others */ } });
}

export const getApiHealth = (): ApiHealth => state;

/** Subscribe to changes. Fires immediately with the current state; returns an unsubscribe. */
export function subscribeApiHealth(fn: (s: ApiHealth) => void): () => void {
  subs.add(fn); fn(state);
  return () => { subs.delete(fn); };
}

const reloadedFor = (): string => { try { return sessionStorage.getItem('slf_reloaded_for') || ''; } catch { return ''; } };
const computeNewBuild = (status: DeployStatus): boolean => chunkFailed || isNewBuild(status, loadedBuild(), reloadedFor());
const visible = (): boolean => { try { return typeof document === 'undefined' || document.visibilityState !== 'hidden'; } catch { return true; } };

// ── Probing /api/health ──────────────────────────────────────────────────────────────────────────────────────────────
/** One probe: did it answer, and when did the answering process start (`boot`, server ms; 0 = an older server, or a body
 *  that could not be read — then nothing is concluded from it). */
async function health(base: string): Promise<{ ok: boolean; boot: number }> {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const tm = window.setTimeout(() => ctl?.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(`${base}/health?t=${Date.now()}`, { cache: 'no-store', signal: ctl?.signal });
    let boot = 0;
    if (res.ok) { try { const o: any = await res.json(); boot = Number(o?.boot) || 0; } catch { /* no body to read */ } }
    return { ok: !!res.ok, boot };
  } catch { return { ok: false, boot: 0 }; } finally { window.clearTimeout(tm); }
}

/** Is a probe of this installation's own API watching a restart (the file says "restarting" now)? */
const watchingRestart = (): boolean => ownWorks() && statusNow(state) === 'restarting';

function scheduleProbe(ms: number): void {
  window.clearTimeout(probeTimer);
  probing = true;
  probeGen++;
  probeTimer = window.setTimeout(() => { void probeOnce(); }, ms);
}

/** Probe now instead of at the next tick — unless one is already on the wire. */
function hastenProbe(): void {
  if (probesInFlight === 0) scheduleProbe(0);
}

function stopProbing(): void {
  window.clearTimeout(probeTimer);
  probeTimer = undefined;
  probing = false;
  probeGen++;
}

/** One answer about the API — a probe's, or a real request's (reportApiOk) — folded into the state. `boot`: the
 *  answering process's start (a probe's answer only; 0 = not known). */
function applyProbe(ok: boolean, boot = 0): { state: ProbeState; emitBack: boolean; stop: boolean } {
  const now = Date.now();
  // A proactive probe (the file says "restarting") can find the outage before any request did: count it as trouble,
  // so the screens waiting on a request still hear "back" afterwards.
  const before = ok || state.probe.probing ? state.probe : probeTrouble(state.probe, now);
  const wasOverlay = state.overlay && state.back !== 'overlay';   // up for the outage, not already saying "back"
  const wasDown = state.probe.down;
  const r = probeStep(before, ok, now);
  if (ok) {
    const back: ApiHealth['back'] = wasOverlay ? 'overlay' : wasDown ? 'banner' : '';
    // A failed request or probe since the last answer (probe.probing is set by both, never by a proactive watch alone).
    const hadTrouble = state.probe.probing || wasOverlay;
    const restartBack = state.restartBack || restartLooksDone(state.status, now + state.serverOffsetMs, hadTrouble, boot);
    // The card says "Back online" for a restart: the pill then stays quiet until the file is idle (noticeLine).
    const backSaid = state.backSaid || (back === 'overlay' && state.restartOutage);
    // `back` only ever starts here; one that is showing runs its course (its timer ends it).
    set({ probe: r.state, ...(back ? { back } : {}), dismissedOverlay: false, restartBack, backSaid });
    if (back) {
      window.clearTimeout(backTimer);
      backTimer = window.setTimeout(() => set({ back: '' }), back === 'overlay' ? backHoldMs(now - overlayShownAt) : BACK_SHOW_MS);
    }
    if (r.emitBack || wasDown) {
      try { window.dispatchEvent(new Event('slf:api-back')); } catch { /* very old browser: the 5 s retries still run */ }
      void pollStatus();   // the new build is likely live now: its name is the reload offer
    }
  } else {
    set({ probe: r.state });
  }
  return r;
}

async function probeOnce(): Promise<void> {
  // A hidden tab sends nothing (review round 1: a tab hidden during a restart went on probing every 3 s for as long as it
  // stayed hidden). The trouble stays on record; the tab probes at once when it comes back to the front (startApiHealth).
  if (!visible()) { stopProbing(); return; }
  const gen = probeGen;
  const mark = outageMark;
  const base = state.probeBase || ownApiBase();
  probesInFlight++;
  let answer = { ok: false, boot: 0 };
  try { answer = await health(base); } finally { probesInFlight--; }
  if (gen !== probeGen) return;   // superseded meanwhile ("Try now", or a real request answered)
  const ok = answer.ok;
  // Asked before this outage began (a watch probe on the wire when the first request failed): the old process may have
  // answered it on its way out. Ask again, now — that probe carries the current mark, so this happens at most once.
  if (ok && state.probe.probing && mark !== outageMark) { scheduleProbe(0); return; }
  // Answered by the process the restart is replacing (it started before the "restarting" write: its event loop was
  // blocked when pm2 signalled it, and it is still accepting for a moment). Not "back", not the restart behind us, not
  // even news: look again at the next tick (review round 2).
  if (ok && base === ownApiBase() && watchingRestart() && bootPredatesRestart(state.status, answer.boot)) { scheduleProbe(PROBE_EVERY_MS); return; }
  const r = applyProbe(ok, answer.boot);
  // The API does not answer: has a deploy been announced since the file was last read? That is what turns "cannot
  // reach" into "updating" — the live check's page learnt it 16 s late. (nginx's static file, not the API.) Always when
  // no read was sent since this outage began (review round 2), else at most once per STATUS_AFTER_PROBE_MS.
  if (!ok && (statusTriedAt < state.probe.since || Date.now() - statusTriedAt >= STATUS_AFTER_PROBE_MS)) readStatusNow();
  // Keep watching while the file still says "restarting": the old process may still be the one answering.
  if (r.stop && !watchingRestart()) stopProbing();
  else scheduleProbe(PROBE_EVERY_MS);
}

/** Watch `base` — at once when `hasten` (the file has just said "restarting"), else from the next tick of a chain that is
 *  already running, or after `first` ms. */
function startProbing(base: string, first: number, hasten = false): void {
  if (!state.probeBase || !probing) set({ probeBase: base });
  if (!probing) scheduleProbe(first);
  else if (hasten) hastenProbe();
}

/** A request to `base` failed the way a restarting server fails (utils/api.ts). Probes /api/health at once and reads the
 *  status file at once (both static/cheap: nothing else is sent to the API); never retries the request itself — the
 *  screens that care listen for slf:api-back. */
export function reportApiTrouble(base: string): void {
  const b = base || ownApiBase();
  const fresh = state.probe.fails === 0;   // no failed probe yet in this outage: nobody has looked at /api/health
  set({ probe: probeTrouble(state.probe, Date.now()), probeBase: b });
  if (!probing) scheduleProbe(0);
  else if (fresh) hastenProbe();          // a chain that only watched (restarting) or is waiting 3 s: look now
  // Read the file unless a read has been SENT since this outage began (failures come in bursts; one read serves them
  // all). A read sent before it began is not enough, however recent, nor one still on the wire: the restart may have
  // been written after it went out (review round 2) — then another follows as soon as it lands.
  if (statusTriedAt < state.probe.since) readStatusNow();
}

/** A request to `base` was answered (utils/api.ts) — any answer but a proxy's 502/503/504: the host is reachable, so
 *  the trouble is over whatever /api/health says. This is also what ends it for a portal pinned to an installation
 *  whose /api/health this page's origin may not read (an older CORS rule), where probing alone would never succeed.
 *  `mark` is apiOutageMark() as that request went out. One sent BEFORE this outage began proves nothing (review round 1):
 *  on pm2's SIGINT the old process stops listening at once but finishes what it already had for up to 8 s, so a slow
 *  search sent just before the restart answers 200 while the new process is not up yet — taking that for "back" flashed
 *  "the update has finished" in the middle of the outage. The probes decide instead. */
export function reportApiOk(base: string, mark?: number): void {
  const b = base || ownApiBase();
  if (!state.probe.probing || b !== state.probeBase) return;
  if (typeof mark === 'number' && mark !== outageMark) return;
  // While this installation is restarting, an answer to a real request does not say WHICH process gave it — the old one,
  // blocked when pm2 signalled it, can still answer a request sent after the outage began. The probes decide: they
  // carry the process's start (review round 2).
  if (b === ownApiBase() && watchingRestart()) return;
  applyProbe(true);
  if (!watchingRestart()) stopProbing();
}

/** "Try now" on the card: probe at once instead of waiting for the next tick. */
export function probeNow(): void {
  if (!state.probe.probing && !probing) return;
  scheduleProbe(0);
}

/** "Continue anyway": the outage goes on showing, as a banner that blocks nothing, until the API is back. */
export function dismissOverlay(): void { set({ dismissedOverlay: true }); }

// ── The status file ──────────────────────────────────────────────────────────────────────────────────────────────────
const isV1 = (text: string): boolean => {
  try { const o = JSON.parse(text); return !!o && typeof o === 'object' && o.v === 1; } catch { return false; }
};

function candidates(): string[] {
  if (statusUrl) return [statusUrl];
  const list = ['/deploy-status.json'];
  // A portal served under /portal/ on another host (the firm's website) maps its own copy there (ops/nginx-site.conf).
  try { if (window.location.pathname.indexOf('/portal') === 0) list.push('/portal/deploy-status.json'); } catch { /* no DOM */ }
  return list;
}

function scheduleStatus(): void {
  window.clearTimeout(statusTimer);
  statusTimer = window.setTimeout(() => { void pollStatus(); }, statusNow(state) === 'idle' ? STATUS_IDLE_MS : STATUS_BUSY_MS);
}

/** Read the status file now — or, when a read is already on the wire, again as soon as it lands: it went out before
 *  whatever made us ask, so its answer may not know about it. */
function readStatusNow(): void {
  if (!started) return;
  if (statusBusy) { statusOwed = true; return; }
  void pollStatus();
}

async function pollStatus(): Promise<void> {
  if (statusBusy) return;
  if (!visible()) { window.clearTimeout(statusTimer); return; }   // the visibility listener reads it again on return
  statusBusy = true;
  statusTriedAt = Date.now();
  try {
    let found: { text: string; date: string | null; modified: string | null; url: string } | null = null;
    let reached = false;
    for (const url of candidates()) {
      try {
        const res = await fetch(`${url}?t=${Date.now()}`, { cache: 'no-store' });
        reached = true;
        if (!res.ok) continue;
        const text = await res.text();
        if (!isV1(text)) continue;
        found = { text, date: res.headers.get('Date'), modified: res.headers.get('Last-Modified'), url };
        break;
      } catch { /* the host itself is unreachable: keep what was last known */ }
    }
    if (!reached) return;
    if (found && !statusUrl) statusUrl = found.url;
    const now = Date.now();
    const status = found ? parseDeployStatus(found.text, { date: found.date, lastModified: found.modified }, now) : parseDeployStatus('', {}, now);
    const dateMs = found?.date ? Date.parse(found.date) : NaN;
    const busy = status.state !== 'idle';
    const prevState = state.status.state;
    // One deploy = one counter: "deploying" then "restarting" keep the first startedAt (a gap longer than a deploy's
    // own lifetime is a new deploy, seen by a tab that slept through the idle in between).
    const prevSince = prevState !== 'idle' ? state.busySince : 0;
    const busySince = !busy ? 0
      : prevSince && (!status.startedAt || (status.startedAt >= prevSince && status.startedAt - prevSince < DEPLOY_STATUS_MAX_AGE_MS)) ? prevSince
        : status.startedAt;
    // restartBack and backSaid belong to one restart: a fresh "restarting" write (from "deploying", or a new deploy), or
    // the file turning idle, starts without them.
    const sameRestart = status.state === 'restarting' && prevState === 'restarting' && busySince === prevSince && status.startedAt === state.status.startedAt;
    set({
      status, statusSeenAt: now, newBuild: computeNewBuild(status),
      busySince, restartBack: sameRestart ? state.restartBack : false, backSaid: sameRestart ? state.backSaid : false,
      busySeenAt: busy ? now : state.busySeenAt,
      idleSeenAt: busy || state.idleSeenAt > state.busySeenAt ? state.idleSeenAt : now,
      serverOffsetMs: isFinite(dateMs) ? dateMs - now : state.serverOffsetMs,
    });
    // The server is about to drop its connections: watch its own API before anything fails — at once on the first sight
    // of this restart (a pending 3-s tick would leave the page silent that long). Not for a restart this tab has already
    // seen through 90 s ago (statusNow: settled), nor on a portal pinned to another installation (review round 2).
    if (watchingRestart()) startProbing(ownApiBase(), 0, !sameRestart);
  } finally {
    statusBusy = false;
    if (statusOwed) { statusOwed = false; void pollStatus(); }
    else scheduleStatus();
  }
}

// ── Start-up ─────────────────────────────────────────────────────────────────────────────────────────────────────────
const isStaticAsset = (el: any): boolean => {
  const tag = String(el?.tagName || '');
  const url = tag === 'SCRIPT' ? String(el.src || '') : tag === 'LINK' ? String(el.href || '') : '';
  return !!url && url.indexOf('/static/') >= 0;
};

function onChunkFailed(): void {
  if (chunkFailed) return;
  chunkFailed = true;
  set({ newBuild: true });
}

/** Idempotent. DeployNotice calls it on mount with the build this page loaded (utils/appUpdate.tsx loadedBuild) and
 *  whether the page works against another installation's API (a portal pinned there — see pinnedElsewhere above). */
export function startApiHealth(loaded?: () => string, elsewhere?: () => boolean): void {
  if (loaded) loadedBuild = loaded;
  if (elsewhere) pinnedElsewhere = elsewhere;
  if (started) return;
  started = true;
  try {
    // A lazily loaded piece of the old build that the deploy has removed: only a reload brings the new one.
    window.addEventListener('error', e => { if (isStaticAsset(e.target)) onChunkFailed(); }, true);
    window.addEventListener('unhandledrejection', e => { if ((e as PromiseRejectionEvent)?.reason?.name === 'ChunkLoadError') onChunkFailed(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      void pollStatus();
      if (state.probe.probing) probeNow();
    });
  } catch { /* no DOM */ }
  void pollStatus();
}
